import IconSearch from "@tvg/fuse-icons/lib/Search";
import { namedUuid } from "@tvg/fuse-utils";
import React, { forwardRef, useRef, useEffect, useCallback } from "react";
import { config, styledInputConfig } from "./configs";
import {
  StyledForm,
  StyledInput,
  StyledInputContainer,
  StyledLabel,
  StyledPlainIconButton,
} from "./search-field.styles";
export interface SearchFieldSubmitHandler {
  (ev: React.FormEvent<HTMLFormElement>, searchTerm: string | null): void;
}

export interface SearchFieldProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onSubmit" | "className" | "id"
  > {
  action?: string;
  dataTestId?: string | null;
  disableAutoFocus?: boolean;
  formId?: string;
  onSubmit?: SearchFieldSubmitHandler;
  onSubmitButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
}

// eslint-disable-next-line prefer-arrow-functions/prefer-arrow-functions
const SearchBar = forwardRef<HTMLFormElement, SearchFieldProps>(function _input(
  {
    action,
    formId,
    dataTestId,
    disableAutoFocus,
    maxLength = styledInputConfig.maxLength,
    name: inputName = styledInputConfig.fieldName,
    onSubmit,
    onSubmitButtonClick,
    ...props
  }: SearchFieldProps,
  ref,
) {
  // Config values
  const { componentName } = config;
  // Ids for input element, helper text, mask button and input icon states
  const inputId = namedUuid(`${componentName}-input`);
  // Refs for input container and input element
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handler to programmtically remove focus outline only when input has focus and is tabbed
  const handleContainerBlur = () => {
    inputContainerRef.current?.classList.remove(
      `${componentName}__input-container--outline`,
    );
  };

  // Handler to programmtically set focus outline only when input has focus and is tabbed
  const handleContainerKeyUp: React.KeyboardEventHandler<HTMLDivElement> = ({
    keyCode,
  }) => {
    const tabKey = 9;
    if (keyCode === tabKey && document.activeElement === inputRef.current)
      inputContainerRef.current?.classList.add(
        `${componentName}__input-container--outline`,
      );
    if (document.activeElement !== inputRef.current)
      inputContainerRef.current?.classList.remove(
        `${componentName}__input-container--outline`,
      );
  };

  const handleSubmit = useCallback<React.FormEventHandler<HTMLFormElement>>(
    (e) => {
      const formData = new FormData(e.currentTarget);
      const searchVal = formData.get(inputName) as string | null;
      onSubmit?.(e, searchVal);
    },
    [inputName, onSubmit],
  );

  useEffect(() => {
    if (!disableAutoFocus) setTimeout(() => inputRef.current?.focus(), 0);
  }, [disableAutoFocus]);

  return (
    <StyledForm
      id={formId}
      data-testid={`${dataTestId}-form`}
      ref={ref}
      action={action}
      onSubmit={handleSubmit}
      role="search"
    >
      <StyledInputContainer
        ref={inputContainerRef}
        onBlur={handleContainerBlur}
        onKeyUp={handleContainerKeyUp}
      >
        <StyledLabel htmlFor={inputId}>Enter search term</StyledLabel>
        <StyledInput
          autoComplete="off"
          name={inputName}
          maxLength={maxLength}
          {...props}
          id={inputId}
          data-cnstrc-search-input
          data-testid={`${dataTestId}-input`}
          ref={inputRef}
        />

        <StyledPlainIconButton
          data-cnstrc-search-submit-btn
          data-testid={`${dataTestId}-submit-btn`}
          ariaLabel="Search Button"
          type="submit"
          icon={<IconSearch />}
          size="small"
          srLabel="Search"
          color="var(--color-global-white)"
          callback={onSubmitButtonClick}
        />
      </StyledInputContainer>
    </StyledForm>
  );
});

export { SearchBar };

export default SearchBar;
